/**
 * LeadImportModal Component - Modal for importing leads from CSV
 */

import React, { useState, useRef } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Alert,
  CircularProgress,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
  Chip,
} from "@mui/material";
import { Upload, Download, Close } from "@mui/icons-material";
import {
  importLeadsFromFile,
  downloadTemplate,
} from "../services/importService.js";

/**
 * LeadImportModal Component
 */
const LeadImportModal = ({ open, onClose, onSuccess }) => {
  const fileInputRef = useRef(null);
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [importResult, setImportResult] = useState(null);
  const [dryRun, setDryRun] = useState(true);

  const handleFileSelect = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      if (
        selectedFile.type !== "text/csv" &&
        !selectedFile.name.endsWith(".csv")
      ) {
        setError("Please select a CSV file");
        return;
      }
      setFile(selectedFile);
      setError(null);
      setImportResult(null);
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      const blob = await downloadTemplate();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "lead_import_template.csv";
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      setError("Failed to download template");
    }
  };

  const handleImport = async () => {
    if (!file) {
      setError("Please select a file first");
      return;
    }

    setLoading(true);
    setError(null);
    setImportResult(null);

    try {
      const result = await importLeadsFromFile(file, dryRun);

      if (result.success) {
        setImportResult(result.data);
        if (!dryRun && onSuccess) {
          onSuccess(result.data);
        }
      } else {
        setError(result.error || "Failed to import leads");
      }
    } catch (err) {
      setError(err.message || "Failed to import leads");
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFile(null);
    setError(null);
    setImportResult(null);
    setDryRun(true);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
    onClose();
  };

  const handleConfirmImport = async () => {
    if (!file) {
      setError("Please select a file first");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Import with dryRun=false to actually persist data
      const result = await importLeadsFromFile(file, false);

      if (result.success) {
        setImportResult(result.data);
        setDryRun(false);
        if (onSuccess) {
          onSuccess(result.data);
        }
      } else {
        setError(result.error || "Failed to import leads");
      }
    } catch (err) {
      setError(err.message || "Failed to import leads");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography variant="h6">Import Leads from CSV</Typography>
          <Button onClick={handleClose} size="small">
            <Close />
          </Button>
        </Box>
      </DialogTitle>

      <DialogContent>
        {loading && <LinearProgress sx={{ mb: 2 }} />}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {!importResult && (
          <>
            <Box sx={{ mb: 3 }}>
              <Typography variant="body2" color="text.secondary" paragraph>
                Import leads from a CSV file. Download the template to see the
                required format.
              </Typography>
              <Button
                startIcon={<Download />}
                onClick={handleDownloadTemplate}
                variant="outlined"
                size="small"
              >
                Download Template
              </Button>
            </Box>

            <Box sx={{ mb: 2 }}>
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv"
                onChange={handleFileSelect}
                style={{ display: "none" }}
              />
              <Button
                variant="outlined"
                startIcon={<Upload />}
                onClick={() => fileInputRef.current?.click()}
                fullWidth
              >
                {file ? file.name : "Select CSV File"}
              </Button>
            </Box>

            {file && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  Selected: {file.name} ({(file.size / 1024).toFixed(2)} KB)
                </Typography>
              </Box>
            )}
          </>
        )}

        {importResult && (
          <Box>
            <Alert severity="success" sx={{ mb: 2 }}>
              Import {dryRun ? "preview" : "completed"} successfully!
            </Alert>

            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle1" gutterBottom>
                Summary
              </Typography>
              <Box sx={{ display: "flex", gap: 1, mb: 2 }}>
                <Chip
                  label={`Total: ${importResult.total || 0}`}
                  color="default"
                />
                <Chip
                  label={`Successful: ${importResult.successful || 0}`}
                  color="success"
                />
                <Chip
                  label={`Failed: ${importResult.failed || 0}`}
                  color="error"
                />
                {importResult.skipped > 0 && (
                  <Chip
                    label={`Skipped: ${importResult.skipped || 0}`}
                    color="warning"
                  />
                )}
              </Box>
            </Box>

            {importResult.details?.failed?.length > 0 && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle2" color="error" gutterBottom>
                  Failed Rows:
                </Typography>
                <List dense>
                  {importResult.details.failed
                    .slice(0, 10)
                    .map((failure, index) => (
                      <ListItem key={index}>
                        <ListItemText
                          primary={`Row ${failure.row}`}
                          secondary={
                            Array.isArray(failure.errors)
                              ? failure.errors.join(", ")
                              : failure.errors
                          }
                        />
                      </ListItem>
                    ))}
                  {importResult.details.failed.length > 10 && (
                    <ListItem>
                      <ListItemText
                        primary={`... and ${importResult.details.failed.length - 10} more`}
                      />
                    </ListItem>
                  )}
                </List>
              </Box>
            )}

            {dryRun && (
              <Alert severity="info" sx={{ mt: 2 }}>
                This is a preview. Click &quot;Confirm Import&quot; to actually
                import the leads.
              </Alert>
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          {importResult && !dryRun ? "Close" : "Cancel"}
        </Button>
        {!importResult && (
          <Button
            onClick={handleImport}
            variant="contained"
            startIcon={loading ? <CircularProgress size={20} /> : <Upload />}
            disabled={!file || loading}
          >
            Preview Import
          </Button>
        )}
        {importResult && dryRun && (
          <Button
            onClick={handleConfirmImport}
            variant="contained"
            startIcon={loading ? <CircularProgress size={20} /> : <Upload />}
            disabled={loading}
          >
            Confirm Import
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default LeadImportModal;
